import { useCallback, useEffect, useState } from "react"

const STORAGE_KEY = "nelo.recent-searches"
const MAX_RECENT = 8

// Push a query to the front, de-duped (case-insensitively) and capped. Pure so
// it's trivially testable; the hook below wires it to localStorage.
const pushRecent = (query: string, list: string[]): string[] => {
  const trimmed = query.trim()
  if (trimmed.length === 0) return list
  const withoutDupe = list.filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase())
  return [trimmed, ...withoutDupe].slice(0, MAX_RECENT)
}

const removeRecent = (query: string, list: string[]): string[] => list.filter((entry) => entry.toLowerCase() !== query.toLowerCase())

const clearRecent = (): string[] => []

const read = (): string[] => {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string").slice(0, MAX_RECENT) : []
  } catch {
    return []
  }
}

const write = (list: string[]) => {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    // Storage can be unavailable (private mode, quota); recents are best-effort.
  }
}

export const useRecentSearches = () => {
  const [recent, setRecent] = useState<string[]>([])

  // Seed from storage after mount so server and first client render match (empty).
  useEffect(() => {
    setRecent(read())
  }, [])

  const add = useCallback((query: string) => {
    setRecent((prev) => {
      const next = pushRecent(query, prev)
      write(next)
      return next
    })
  }, [])

  const remove = useCallback((query: string) => {
    setRecent((prev) => {
      const next = removeRecent(query, prev)
      write(next)
      return next
    })
  }, [])

  const clear = useCallback(() => {
    setRecent(clearRecent())
    write([])
  }, [])

  return { recent, add, remove, clear }
}
